import { z } from "zod";

import type { AssessmentElement } from "../types";

/**
 * Generate a dynamic Zod schema from assessment elements.
 * All form fields use strings (React Aria requirement).
 * Use adaptFormDataToSubmission() to convert rating values back to numbers for API.
 *
 * @param elements - Assessment elements from the API
 * @returns Zod schema for form validation
 */
export const createAssessmentSchema = (elements: AssessmentElement[]) => {
  if (!elements || elements.length === 0) {
    return z.object({});
  }

  // Build an array of [key, schema] pairs, then convert to object
  // This preserves literal types better than Record<string, T>
  const schemaPairs = elements.map((element) => {
    const errorMessage = element.isRequired ? "Please make a selection." : undefined;

    let schema: z.ZodTypeAny;

    switch (element.type) {
      case "checkbox":
        // Multi-select: array of strings, at least 1 required if isRequired
        schema = element.isRequired
          ? z.array(z.string()).min(1, "Please select at least one option.")
          : z.array(z.string());
        break;

      case "radiogroup":

      case "rating":
        // Single-select: string value (non-empty if required)
        schema = element.isRequired
          ? z.string({ message: errorMessage }).min(1, errorMessage)
          : z.string().optional();
        break;

      default:
        schema = z.string();
    }

    return [element.name, schema] as const;
  });

  const schemaShape = Object.fromEntries(schemaPairs);

  return z.object(schemaShape);
};
